/**
 * 动态表单渲染器
 * -----------------------------------------------------------------------------
 * 输入是服务端下发的 `FormSchemaView`，输出是受控的表单值与字段级错误。
 * **页面不写任何字段名**：加字段、改必填、改条件显示都只改后台配置。
 *
 * 分工：
 *   - 渲染与校验规则在 `./logic.ts`（可单测，不依赖 DOM）
 *   - 本文件只负责把逻辑结果画成 antd-mobile 组件
 *
 * 交互约定：
 *   - 字段级错误**只在 `onBlur`**（或父级显式传入）时出现，不在每次按键时抖动
 *   - 条件隐藏的字段不渲染；其取值由父级在提交前用 `prunedAnswers` 剔除
 *   - `locked` 字段禁改；`review` 字段可改但带「需复审」提示
 */
import {
  type AnswerError,
  FieldType,
  type FormFieldDef,
  type FormFieldOption,
  type FormSchemaView,
  REGION_DETAIL_MAX_LENGTH,
  uploadMaxCount,
} from '@enroll/shared';
import { Button, Checkbox, DatePicker, Input, Picker, Radio, Switch, TextArea } from 'antd-mobile';
import { useCallback, useMemo, useState } from 'react';
import { useDismissBackGuard } from '../../lib/use-dismiss-back-guard';
import { Card, KV } from '../layout';
import { IdCardField, type IdCardOcrResult } from './IdCardField';
import {
  type Answers,
  buildGroupedFields,
  collectFieldErrors,
  computePriceBreakdown,
  firstErrorByKey,
  formatCentsText,
  isNameField,
  maxLengthOf,
  optionPriceText,
  resolveFieldEditing,
} from './logic';
import { PhoneVerifyField } from './PhoneVerifyField';
import { RegionField } from './RegionField';
import { SignatureCanvas } from './SignatureCanvas';
import { UploadField } from './UploadField';

export interface FormRendererProps {
  schema: FormSchemaView;
  value: Answers;
  onChange: (next: Answers) => void;
  /** 外部错误（提交失败 / 步骤切换整表校验），键为 `field.key` */
  errors?: AnswerError[];
  /** 提交后编辑分级：`true` 时按 `field.editable` 处理只读与复审提示 */
  graded?: boolean;
  /**
   * 本次**已获准修改**的字段 key（`Registration.grantedFields`，服务端已按 7 天有效期过滤）。
   *
   * 只有进这张名单的 `review` 字段才可改；`locked` 字段即便在名单里也仍然禁改
   * （判定在 `resolveFieldEditing` 里，顺序不能反）。
   */
  grantedKeys?: readonly string[];
  /** 整体禁用（只读回显） */
  disabled?: boolean;
  /** 手机号短信验证通过时上报（供「身份核验」页显示验证状态）；带出通过的那个号码 */
  onPhoneVerified?: (verifiedPhone: string) => void;
  /**
   * 不在本步渲染的字段 key。
   *
   * 「身份证 / 手机号 / 姓名」这些依赖第三方能力的字段统一挪到「身份核验」步骤里，
   * 和证件照放在一起（2026-10-18 拍板）。它们的取值仍然写进同一份表单答案，
   * 只是不在这里画 —— 所以这里只过滤渲染，不影响必填与提交。
   */
  omitKeys?: readonly string[];
  /**
   * 只渲染这些分组（`group.key`）。为空则渲染全部分组。
   *
   * 分步表单靠它切页：第 1 步「基本信息」= 除材料组以外的全部，
   * 第 2 步「材料与承诺」= 材料组。分组是后台配的，所以这里传 key 而不是写死字段。
   */
  onlyGroupKeys?: readonly string[];
  /** 多个分组被合并渲染时的卡片标题（不传则沿用第一个分组的标签） */
  mergedLabel?: string;
  /** 报名单 id：身份证 OCR 需要它（草稿态已存在） */
  registrationId?: string;
  /** 身份证人像面识别成功时上报（供「身份核验」页复用这次识别） */
  onIdCardRecognized?: (result: IdCardOcrResult) => void;
}

/** 分组内的渲染单元：普通字段一行，身份证则是「姓名 + 号码 + 正反面」一张卡 */
type FormEntry =
  | { kind: 'field'; field: FormFieldDef }
  | { kind: 'idcard'; idField: FormFieldDef; nameField?: FormFieldDef };

/**
 * 版面块：身份证要**独占一张卡**，不能塞进「基本信息」卡里（会变成卡中卡）。
 * 所以把每组的 entries 顺着切：连续普通字段聚成一个卡片块，遇到身份证就单独成块。
 */
type FormBlock =
  | { kind: 'card'; key: string; label: string; fields: FormFieldDef[] }
  | { kind: 'idcard'; key: string; label: string; idField: FormFieldDef; nameField?: FormFieldDef };

/**
 * 把同一组里的「姓名」与「身份证」合成一个渲染单元。
 *
 * 判定刻意保守：必须是 `text` 型且 `system: true`（系统字段），key 命中已知名字或标签就是「姓名」。
 * 找不到姓名就单独渲染身份证，绝不因为匹配失败而漏掉字段。
 */
function toEntries(fields: FormFieldDef[]): FormEntry[] {
  const idField = fields.find((field) => field.type === FieldType.IDCARD);
  if (!idField) return fields.map((field) => ({ kind: 'field', field }));

  const nameField = fields.find((field) => field !== idField && isNameField(field));

  const entries: FormEntry[] = [];
  let placed = false;
  for (const field of fields) {
    if (field === idField || field === nameField) {
      if (!placed) {
        entries.push({ kind: 'idcard', idField, nameField });
        placed = true;
      }
      continue;
    }
    entries.push({ kind: 'field', field });
  }
  return entries;
}

/** entries → 版面块（身份证独立成卡，其余按卡片聚拢） */
function toBlocks(groupKey: string, groupLabel: string, entries: FormEntry[]): FormBlock[] {
  const blocks: FormBlock[] = [];
  let pending: FormFieldDef[] = [];

  const flush = () => {
    if (pending.length === 0) return;
    blocks.push({
      kind: 'card',
      key: `${groupKey}-${pending[0]?.key ?? 'card'}`,
      label: groupLabel,
      fields: pending,
    });
    pending = [];
  };

  for (const entry of entries) {
    if (entry.kind === 'field') {
      pending.push(entry.field);
      continue;
    }
    flush();
    blocks.push({
      kind: 'idcard',
      key: `${groupKey}-idcard-${entry.idField.key}`,
      label: groupLabel,
      idField: entry.idField,
      nameField: entry.nameField,
    });
  }
  flush();
  return blocks;
}

/** `Date` → `YYYY-MM-DD`（服务端 `DATE_ONLY_PATTERN` 要求） */
export function formatDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDateOnly(value: unknown): Date | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** 分项报价与合计（select / radio 的带价选项之和 − 减免） */
export function PriceSummary({
  schema,
  value,
  reductionCents = 0,
}: {
  schema: FormSchemaView;
  value: Answers;
  reductionCents?: number;
}) {
  const breakdown = computePriceBreakdown(schema.fields, value, reductionCents);
  return (
    <Card title="费用明细" tight>
      {breakdown.lines.length === 0 ? (
        <KV label="计费项" value="尚未选择计费项" soft />
      ) : (
        breakdown.lines.map((line) => (
          <KV
            key={line.key}
            label={`${line.label} · ${line.optionLabel}`}
            value={<span className="mono-num">{formatCentsText(line.amountCents)}</span>}
          />
        ))
      )}
      {breakdown.reductionCents > 0 ? (
        <KV
          label="减免"
          value={<span className="mono-num">-{formatCentsText(breakdown.reductionCents)}</span>}
        />
      ) : null}
      <KV
        label="应缴合计"
        lined
        value={
          <strong className="mono-num price-total">
            {formatCentsText(breakdown.payableCents)}
          </strong>
        }
      />
    </Card>
  );
}

function FieldHint({ hint, disabled }: { hint: string | null; disabled: boolean }) {
  if (!hint) return null;
  return (
    <div className={`field-hint${disabled ? ' field-hint--locked' : ''}`}>
      {disabled ? '🔒 ' : 'ⓘ '}
      {hint}
    </div>
  );
}

function OptionLabel({ option }: { option: FormFieldOption }) {
  const price = optionPriceText(option);
  return (
    <span className="option-label">
      <span>{option.label}</span>
      {price ? <span className="mono-num option-price">{price}</span> : null}
    </span>
  );
}

interface FieldControlProps {
  field: FormFieldDef;
  value: unknown;
  disabled: boolean;
  invalid: boolean;
  onChange: (next: unknown) => void;
  /** 失焦：异步控件可把刚产生的新值带上来，避免用旧值校验 */
  onBlur: (next?: unknown) => void;
  onPhoneVerified?: (verifiedPhone: string) => void;
  /** 上传类控件需要它把资产挂到报名单上（提交时才清得掉没被引用的旧附件） */
  registrationId?: string;
}

function FieldControl({
  field,
  value,
  disabled,
  invalid,
  onChange,
  onBlur,
  onPhoneVerified,
  registrationId,
}: FieldControlProps) {
  const options = field.options ?? [];
  // 选项型字段（下拉 / 日期 / 地区）走「请选择」，输入型才用「请输入」——
  // 否则真实 schema 没配 placeholder 时会出现「请输入证件类型」这种错话。
  const pickVerb =
    field.type === FieldType.SELECT ||
    field.type === FieldType.DATE ||
    field.type === FieldType.REGION;
  const placeholder = field.placeholder ?? `${pickVerb ? '请选择' : '请输入'}${field.label}`;

  /**
   * 下拉 / 日期选择器改成**受控**（`visible`），以便各占一层返回守卫。
   *
   * 非受控的 `actions.open` 拿不到「开着没有」，全面屏右滑 / 安卓返回键会穿过浮层，
   * 直接落到表单页的「退出填写？」上 —— 用户只想关一个选择器，却被问要不要退出填写。
   * 一个 `FieldControl` 只会渲染一个选择器，所以共用一份 `open` 状态就够。
   *
   * ⚠️ 受控之后 `actions.open` 会失效（内部 `setVisible` 打不穿受控值），
   * 打开/关闭必须自己改 state。
   */
  const [pickerOpen, setPickerOpen] = useState(false);
  useDismissBackGuard(pickerOpen, () => setPickerOpen(false));

  if (field.type === FieldType.RADIO) {
    // `Radio.Group` 只是 Context Provider，不渲染包裹元素 —— className 挂不上，
    // 所以胶囊容器得自己套一层 div。
    return (
      <Radio.Group
        value={typeof value === 'string' ? value : null}
        disabled={disabled}
        onChange={onChange}
      >
        <div className="choice-pills">
          {options.map((option) => (
            <Radio key={option.value} value={option.value}>
              <OptionLabel option={option} />
            </Radio>
          ))}
        </div>
      </Radio.Group>
    );
  }

  if (field.type === FieldType.CHECKBOX) {
    const list = Array.isArray(value) ? (value as string[]) : [];
    // 多选走**勾选行**而不是胶囊：真实 schema 里多选基本都是「承诺条款」这种长句，
    // 胶囊会把两行字撑成一个像按钮的大块，还容易顶破卡片。
    return (
      <Checkbox.Group value={list} disabled={disabled} onChange={onChange}>
        <div className="choice-list">
          {options.map((option) => (
            <Checkbox key={option.value} value={option.value}>
              <OptionLabel option={option} />
            </Checkbox>
          ))}
        </div>
      </Checkbox.Group>
    );
  }

  if (field.type === FieldType.SELECT) {
    return (
      <Picker
        visible={pickerOpen}
        columns={[options.map((option) => ({ label: option.label, value: option.value }))]}
        value={typeof value === 'string' && value ? [value] : []}
        onConfirm={(picked) => {
          // 选中值必须**带给 onBlur**：onChange 之后闭包里的 value 还是旧值，
          // 用旧值校验会当场报「必填项未填写」（明明刚选完）
          const next = typeof picked[0] === 'string' ? picked[0] : undefined;
          onChange(next);
          onBlur(next);
          setPickerOpen(false);
        }}
        onCancel={() => setPickerOpen(false)}
        title={field.label}
      >
        {(items) => {
          const current = items[0];
          return (
            <Button
              fill="none"
              size="small"
              disabled={disabled}
              onClick={disabled ? undefined : () => setPickerOpen(true)}
              className={`picker-trigger${invalid ? ' picker-trigger--invalid' : ''}`}
            >
              {current ? current.label : placeholder}
            </Button>
          );
        }}
      </Picker>
    );
  }

  if (field.type === FieldType.DATE) {
    const date = parseDateOnly(value);
    return (
      <DatePicker
        visible={pickerOpen}
        precision="day"
        value={date}
        onConfirm={(picked) => {
          const next = formatDateOnly(picked);
          onChange(next);
          onBlur(next);
          setPickerOpen(false);
        }}
        onCancel={() => setPickerOpen(false)}
        title={field.label}
      >
        {() => (
          <Button
            fill="none"
            size="small"
            disabled={disabled}
            onClick={disabled ? undefined : () => setPickerOpen(true)}
            className={`picker-trigger${invalid ? ' picker-trigger--invalid' : ''}`}
          >
            {date ? formatDateOnly(date) : placeholder}
          </Button>
        )}
      </DatePicker>
    );
  }

  if (field.type === FieldType.BOOLEAN) {
    return (
      <Switch
        checked={value === true}
        disabled={disabled}
        onChange={(checked) => {
          onChange(checked);
          onBlur(checked);
        }}
      />
    );
  }

  if (field.type === FieldType.UPLOAD) {
    // 表单附件（C-23）——上传走 `POST /api/h5/uploads`，
    // 表单值 = assetId（单份）或 assetId 数组（`field.multiple`，D-06）。
    // 上限必须走 `uploadMaxCount()` 换算：三端各写一次 `field.maxCount ?? 5`
    // 迟早有一端把脏配置（0 / 超硬上限）当成有效值。
    return (
      <UploadField
        value={value}
        disabled={disabled}
        registrationId={registrationId}
        multiple={field.multiple === true}
        maxCount={uploadMaxCount(field)}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }

  if (field.type === FieldType.SIGNATURE) {
    // 手写签名承诺（C-23）—— canvas 手写 → PNG → 上传，值 = assetId 字符串。
    return (
      <SignatureCanvas
        value={value}
        disabled={disabled}
        registrationId={registrationId}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }

  if (field.type === FieldType.REGION) {
    // 行政区划三级选择 + 详细地址（D-05 裁决）—— 复合控件，值形如 `RegionValue`。
    // `detailMaxLength` 必须与校验器同源（字段声明的 `validation.maxLength`，
    // 未声明时落到共享常量），否则会出现「UI 让填、校验器拦」。
    return (
      <RegionField
        value={value}
        disabled={disabled}
        invalid={invalid}
        detailMaxLength={maxLengthOf(field) ?? REGION_DETAIL_MAX_LENGTH}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }

  if (field.type === FieldType.TEXTAREA) {
    const max = maxLengthOf(field);
    return (
      <TextArea
        value={typeof value === 'string' ? value : ''}
        placeholder={placeholder}
        disabled={disabled}
        rows={3}
        maxLength={max ?? undefined}
        showCount={max !== null}
        onChange={onChange}
        // 不传事件：原生 onBlur 会把 FocusEvent 当"新值"，见 `blurField` 的注释
        onBlur={() => onBlur()}
      />
    );
  }

  if (field.type === FieldType.NUMBER) {
    return (
      <Input
        type="number"
        value={value === undefined || value === null ? '' : String(value)}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(raw) => onChange(raw === '' ? undefined : Number(raw))}
        onBlur={() => onBlur()}
      />
    );
  }

  if (field.type === FieldType.PHONE) {
    // 手机号 + 短信验证（附件1「手机号真实性验证」）：
    // 提交前服务端强门槛 PHONE-005，这里提供发码 / 验证交互。
    return (
      <PhoneVerifyField
        value={value}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        onVerified={onPhoneVerified}
      />
    );
  }

  if (field.type === FieldType.IDCARD) {
    return (
      <Input
        type="text"
        maxLength={18}
        value={typeof value === 'string' ? value : ''}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(raw) => onChange(raw.toUpperCase())}
        onBlur={() => onBlur()}
      />
    );
  }

  return (
    <Input
      value={typeof value === 'string' ? value : ''}
      placeholder={placeholder}
      disabled={disabled}
      onChange={onChange}
      // 不传事件：原生 onBlur 会把 FocusEvent 当"新值"，见 `blurField` 的注释
      onBlur={() => onBlur()}
    />
  );
}

export function FormRenderer({
  schema,
  value,
  onChange,
  errors,
  graded = false,
  grantedKeys,
  disabled = false,
  onPhoneVerified,
  omitKeys,
  onlyGroupKeys,
  mergedLabel,
  registrationId,
  onIdCardRecognized,
}: FormRendererProps) {
  const [touched, setTouched] = useState<Record<string, string>>({});

  const externalErrors = useMemo(() => firstErrorByKey(errors ?? []), [errors]);
  const errorMap = useMemo(() => ({ ...touched, ...externalErrors }), [touched, externalErrors]);
  /** 过滤掉「不归这一步管」的字段后再分组（省略的是渲染，不是数据） */
  const blocks = useMemo(() => {
    const omit = new Set(omitKeys ?? []);
    const visible = omit.size === 0 ? schema.fields : schema.fields.filter((f) => !omit.has(f.key));
    const groups = buildGroupedFields({ ...schema, fields: visible }, value);
    const only = onlyGroupKeys && onlyGroupKeys.length > 0 ? new Set(onlyGroupKeys) : null;
    const picked = only ? groups.filter((group) => only.has(group.key)) : groups;
    // 落到同一步的多个分组合成一张卡：分步骤讲的是"这一步做什么"，
    // 不是把后台的分组标签原样搬成小标题（「基本信息」下面再挂个「报考与职业信息」很割裂）
    const merged =
      only && picked.length > 1
        ? [
            {
              key: 'merged',
              label: mergedLabel ?? picked[0]?.label ?? '',
              fields: picked.flatMap((group) => group.fields),
            },
          ]
        : picked;
    return merged.flatMap((group) => toBlocks(group.key, group.label, toEntries(group.fields)));
  }, [schema, value, omitKeys, onlyGroupKeys, mergedLabel]);

  const setField = useCallback(
    (key: string, next: unknown) => {
      onChange({ ...value, [key]: next });
    },
    [onChange, value],
  );

  /**
   * 失焦校验。
   *
   * `next` 是**刚刚产生的新值**：上传 / 签名这类异步控件在 `onChange` 之后立刻 `onBlur`，
   * 此时闭包里的 `value` 还没更新，用旧值校验会当场报一条「必填项未填写」——
   * 用户明明刚签完却看到红字。所以允许调用方把新值带进来。
   *
   * ⚠️ **必须过滤 DOM 事件对象**（2026-10-20 缺陷）：原生 `onBlur` 的回调会把
   * `FocusEvent` 传进来，一旦被当成"新值"，`typeof value !== 'string'` 就会判成
   * 「必须是文本」—— 表现是**每个文本框只要输入内容再失焦就报红**。
   * 调用点已经统一改成不传事件（见 `FieldControl` 里的 `commit`），这里再兜一层，
   * 免得以后又有人把 `onBlur` 直接绑到 DOM 控件上。
   */
  const blurField = useCallback(
    (field: FormFieldDef, next?: unknown) => {
      const incoming = typeof Event !== 'undefined' && next instanceof Event ? undefined : next;
      const current = incoming === undefined ? value[field.key] : incoming;
      const found = collectFieldErrors(field, current, false);
      setTouched((prev) => {
        const nextMap = { ...prev };
        const first = found[0];
        if (first) nextMap[field.key] = first.message;
        else delete nextMap[field.key];
        return nextMap;
      });
    },
    [value],
  );

  return (
    <div className="form-renderer">
      {blocks.map((block) => {
        if (block.kind === 'idcard') {
          const idEditing = resolveFieldEditing(block.idField, graded, grantedKeys);
          const nameEditing = block.nameField
            ? resolveFieldEditing(block.nameField, graded, grantedKeys)
            : null;
          const nameKey = block.nameField?.key;
          return (
            <IdCardField
              key={block.key}
              registrationId={registrationId}
              nameField={block.nameField}
              idField={block.idField}
              name={nameKey ? String(value[nameKey] ?? '') : ''}
              idCardNo={String(value[block.idField.key] ?? '')}
              disabled={disabled || (idEditing.disabled && (nameEditing?.disabled ?? true))}
              nameError={nameKey ? errorMap[nameKey] : undefined}
              idError={errorMap[block.idField.key]}
              hints={[nameEditing?.hint, idEditing.hint].filter((hint): hint is string =>
                Boolean(hint),
              )}
              onRecognized={onIdCardRecognized}
              onChange={(patch) => {
                const next = { ...value };
                if (patch.name !== undefined && nameKey) next[nameKey] = patch.name;
                if (patch.idCardNo !== undefined) next[block.idField.key] = patch.idCardNo;
                onChange(next);
              }}
            />
          );
        }
        return (
          <Card key={block.key} title={block.label}>
            {block.fields.map((field) => {
              const editing = resolveFieldEditing(field, graded, grantedKeys);
              const fieldDisabled = disabled || editing.disabled;
              const message = errorMap[field.key];
              /**
               * `data-field-key` 是「续填定位」的锚点（见 `EnrollForm` 的 `resumeAnchorKey`）。
               * 表单是 Schema 驱动的，字段顺序由后台配，外层拿不到 DOM 位置，
               * 只能靠这个属性回查 —— 别删，删了续填就只能落回页面顶部。
               */
              return (
                <div key={field.key} className="field" data-field-key={field.key}>
                  <span className="field__label">
                    {field.label}
                    {field.required ? <em className="field__required">*</em> : null}
                  </span>
                  {field.help ? <span className="field__help">{field.help}</span> : null}
                  <div className="field__control">
                    <FieldControl
                      field={field}
                      value={value[field.key]}
                      disabled={fieldDisabled}
                      invalid={Boolean(message)}
                      onChange={(next) => setField(field.key, next)}
                      onBlur={(next) => blurField(field, next)}
                      onPhoneVerified={onPhoneVerified}
                      registrationId={registrationId}
                    />
                    <FieldHint hint={editing.hint} disabled={editing.disabled} />
                    {message ? <div className="field-error">{message}</div> : null}
                  </div>
                </div>
              );
            })}
          </Card>
        );
      })}
    </div>
  );
}
